import { supabase } from "./supabase.ts";

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  (import.meta.env.VITE_API_URL as string | undefined) ??
  "https://api.zedule.app";

// Local dev bypass: when set, the pool worker accepts this instead of a real JWT.
// See apps/pool-worker/src/middleware/auth.ts
const DEV_BYPASS_USER_ID = import.meta.env.VITE_DEV_BYPASS_USER_ID as string | undefined;

async function authFetch(path: string, init?: RequestInit): Promise<Response> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init?.headers as Record<string, string> ?? {}),
  };

  if (DEV_BYPASS_USER_ID) {
    // Local dev — bypass Supabase Auth entirely.
    headers["X-Dev-Bypass"] = DEV_BYPASS_USER_ID;
  } else {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  // Real API paths are /api/dashboard/:slug/... — pages call this helper with "/:slug/..."
  const res = await fetch(`${API_BASE}/api/dashboard${path}`, {
    ...init,
    headers,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const e = (body as { error?: unknown }).error;
    // Gateway returns either { error: "msg" } (legacy) or
    // { error: { code, message, status } } (the new envelope shape).
    const msg =
      typeof e === "string"
        ? e
        : e && typeof e === "object" && "message" in (e as object)
          ? String((e as { message: unknown }).message)
          : res.statusText;
    // Attach the parsed error envelope + status so callers can branch on a
    // structured code (e.g. EMAIL_TAKEN / HAS_APPOINTMENTS) without parsing
    // the message text. Existing callers that only read `.message` are
    // unaffected.
    const err = new Error(msg) as Error & { error?: unknown; status?: number };
    if (e && typeof e === "object") err.error = e;
    err.status = res.status;
    throw err;
  }

  return res;
}

// ── Appointments ──────────────────────────────────────────────────────────────

export async function getAppointments(slug: string, params?: Record<string, string>) {
  const qs = params ? "?" + new URLSearchParams(params).toString() : "";
  const res = await authFetch(`/${slug}/appointments${qs}`);
  const body = (await res.json()) as { data?: unknown } | unknown[];
  // Backend returns { data, pagination } — unwrap to array for caller compatibility.
  if (Array.isArray(body)) return body;
  return Array.isArray((body as { data?: unknown }).data)
    ? ((body as { data: unknown[] }).data)
    : [];
}

export async function updateAppointment(slug: string, id: string, body: unknown) {
  const res = await authFetch(`/${slug}/appointments/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  return res.json();
}

export async function duplicateAppointment(
  slug: string,
  id: string,
  body: { start_at: string; staff_id?: string },
) {
  const res = await authFetch(`/${slug}/appointments/${id}/duplicate`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return res.json();
}

export async function createAppointment(slug: string, body: unknown) {
  const res = await authFetch(`/${slug}/appointments`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return res.json();
}

// ── Services ──────────────────────────────────────────────────────────────────

export async function getServices(slug: string) {
  const res = await authFetch(`/${slug}/services`);
  return res.json();
}

export async function createService(slug: string, body: unknown) {
  const res = await authFetch(`/${slug}/services`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return res.json();
}

export async function updateService(slug: string, id: string, body: unknown) {
  const res = await authFetch(`/${slug}/services/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  return res.json();
}

export async function deleteService(slug: string, id: string) {
  await authFetch(`/${slug}/services/${id}`, { method: "DELETE" });
}

// ── Staff ─────────────────────────────────────────────────────────────────────

export async function getStaff(slug: string) {
  const res = await authFetch(`/${slug}/staff`);
  return res.json();
}

export async function createStaff(slug: string, body: unknown) {
  const res = await authFetch(`/${slug}/staff`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return res.json();
}

export async function updateStaff(slug: string, id: string, body: unknown) {
  const res = await authFetch(`/${slug}/staff/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  return res.json();
}

export async function getStaffHours(slug: string, staffId: string) {
  const res = await authFetch(`/${slug}/staff/${staffId}/hours`);
  return res.json();
}

// Bulk: every active staff member's working_hours rows in one call. Used by
// the calendar to avoid N+1 fan-out (one /staff/:id/hours per staff).
export async function getAllStaffHours(slug: string) {
  const res = await authFetch(`/${slug}/staff/working-hours`);
  return res.json();
}

export async function updateStaffHours(slug: string, staffId: string, body: unknown) {
  const res = await authFetch(`/${slug}/staff/${staffId}/hours`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
  return res.json();
}

export async function getBlockedTimes(slug: string, staffId: string) {
  const res = await authFetch(`/${slug}/staff/${staffId}/blocked-times`);
  return res.json();
}

// Bulk: every active staff member's blocked_times rows in one call, optionally
// filtered to overlap the given ISO range. Replaces the per-staff fan-out.
export async function getAllBlockedTimes(
  slug: string,
  range?: { range_start: string; range_end: string },
) {
  const qs = range
    ? "?" + new URLSearchParams(range).toString()
    : "";
  const res = await authFetch(`/${slug}/staff/blocked-times${qs}`);
  return res.json();
}

export async function createBlockedTime(slug: string, staffId: string, body: unknown) {
  const res = await authFetch(`/${slug}/staff/${staffId}/blocked-times`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return res.json();
}

export async function deleteBlockedTime(slug: string, staffId: string, id: string) {
  await authFetch(`/${slug}/staff/${staffId}/blocked-times/${id}`, { method: "DELETE" });
}

// ── Customers ─────────────────────────────────────────────────────────────────

export async function getCustomers(slug: string, search?: string) {
  const qs = search ? `?search=${encodeURIComponent(search)}` : "";
  const res = await authFetch(`/${slug}/customers${qs}`);
  const body = (await res.json()) as { data?: unknown } | unknown[];
  if (Array.isArray(body)) return body;
  return Array.isArray((body as { data?: unknown }).data)
    ? (body as { data: unknown[] }).data
    : [];
}

export async function getCustomerHistory(slug: string, customerId: string) {
  const res = await authFetch(`/${slug}/customers/${customerId}/history`);
  return res.json();
}

export type CustomerInput = { name: string; email?: string; phone?: string; notes?: string };

// authFetch throws on non-ok with `.error` = the parsed `{ code, message }`
// envelope, so callers branch on `err.error?.code` (EMAIL_TAKEN / HAS_APPOINTMENTS).
export async function createCustomer(slug: string, body: CustomerInput): Promise<{ id: string }> {
  const res = await authFetch(`/${slug}/customers`, { method: "POST", body: JSON.stringify(body) });
  return res.json();
}

export async function updateCustomer(
  slug: string,
  id: string,
  body: Partial<CustomerInput>,
): Promise<{ success: boolean }> {
  const res = await authFetch(`/${slug}/customers/${id}`, { method: "PATCH", body: JSON.stringify(body) });
  return res.json();
}

export async function deleteCustomer(slug: string, id: string): Promise<{ success: boolean }> {
  const res = await authFetch(`/${slug}/customers/${id}`, { method: "DELETE" });
  return res.json();
}

// ── Products ──────────────────────────────────────────────────────────────────

export interface Product { id: string; name: string; price_cents: number; type: string; taxable: number; active: number; stock_qty: number | null; low_stock_threshold: number | null }
export type ProductInput = { name: string; price_cents: number; type?: string; taxable?: boolean; stock_qty?: number | null; low_stock_threshold?: number | null };

export async function getProducts(slug: string, opts?: { search?: string; activeOnly?: boolean }): Promise<Product[]> {
  const qs = new URLSearchParams();
  if (opts?.search) qs.set("search", opts.search);
  if (opts?.activeOnly) qs.set("active", "1");
  const res = await authFetch(`/${slug}/products${qs.toString() ? `?${qs}` : ""}`);
  const body = (await res.json()) as { data?: Product[] };
  return Array.isArray(body.data) ? body.data : [];
}
export async function createProduct(slug: string, body: ProductInput): Promise<{ id: string }> {
  const res = await authFetch(`/${slug}/products`, { method: "POST", body: JSON.stringify(body) });
  return res.json();
}
export async function updateProduct(slug: string, id: string, body: Partial<ProductInput & { active: boolean }>): Promise<{ success: boolean }> {
  const res = await authFetch(`/${slug}/products/${id}`, { method: "PATCH", body: JSON.stringify(body) });
  return res.json();
}
export async function adjustStock(slug: string, id: string, body: { mode: "set" | "delta"; value: number; reason?: string; note?: string }): Promise<{ stock_qty: number }> {
  const res = await authFetch(`/${slug}/products/${id}/stock`, { method: "POST", body: JSON.stringify(body) });
  return res.json();
}

// ── Payments ────────────────────────────────────────────────────────────────
export type PaymentConfig = { connected: boolean; default_currency: string; publishable_key: string | null };

export async function getPaymentConfig(slug: string): Promise<PaymentConfig> {
  const res = await authFetch(`/${slug}/payments/config`);
  return res.json();
}
export async function connectStripe(slug: string, body: { secret_key: string; default_currency?: string }): Promise<{ connected: boolean }> {
  const res = await authFetch(`/${slug}/payments/config`, { method: "PUT", body: JSON.stringify(body) });
  return res.json();
}
export async function disconnectStripe(slug: string): Promise<{ success: boolean }> {
  const res = await authFetch(`/${slug}/payments/config`, { method: "DELETE" });
  return res.json();
}
export async function getPayments(slug: string) {
  const res = await authFetch(`/${slug}/payments`);
  const body = (await res.json()) as { data?: unknown[] };
  return Array.isArray(body.data) ? body.data : [];
}
export async function createCheckout(
  slug: string,
  body: { amount_cents: number; currency?: string; description?: string; appointment_id?: string; customer_id?: string; kind?: string; bill_id?: string },
): Promise<{ id: string; url: string }> {
  const res = await authFetch(`/${slug}/payments/checkout`, { method: "POST", body: JSON.stringify(body) });
  return res.json();
}
export async function refundPayment(slug: string, id: string): Promise<{ success: boolean }> {
  const res = await authFetch(`/${slug}/payments/${id}/refund`, { method: "POST" });
  return res.json();
}
export async function getTerminalConfig(slug: string): Promise<{ location_id: string | null }> {
  const res = await authFetch(`/${slug}/payments/terminal/config`);
  return res.json();
}
export async function createTerminalLocation(slug: string, body: { display_name: string; line1: string; city: string; country: string; postal_code?: string; state?: string }): Promise<{ location_id: string }> {
  const res = await authFetch(`/${slug}/payments/terminal/location`, { method: "POST", body: JSON.stringify(body) });
  return res.json();
}
export async function registerReader(slug: string, body: { registration_code: string; label?: string }): Promise<{ reader: unknown }> {
  const res = await authFetch(`/${slug}/payments/terminal/register-reader`, { method: "POST", body: JSON.stringify(body) });
  return res.json();
}
export async function terminalConnectionToken(slug: string): Promise<{ secret: string }> {
  const res = await authFetch(`/${slug}/payments/terminal/connection-token`, { method: "POST" });
  return res.json();
}
export async function createTerminalPaymentIntent(slug: string, body: { amount_cents: number; currency?: string; description?: string; appointment_id?: string; customer_id?: string; kind?: string; bill_id?: string }): Promise<{ payment_id: string; client_secret: string; payment_intent_id: string }> {
  const res = await authFetch(`/${slug}/payments/terminal/payment-intent`, { method: "POST", body: JSON.stringify(body) });
  return res.json();
}
export type DepositPolicy = { deposit_enabled: boolean; deposit_kind: "percent" | "fixed"; deposit_value: number };
export async function getDepositPolicy(slug: string): Promise<DepositPolicy> {
  const res = await authFetch(`/${slug}/payments/deposit-policy`);
  return res.json();
}
export async function setDepositPolicy(slug: string, body: DepositPolicy): Promise<{ success: boolean }> {
  const res = await authFetch(`/${slug}/payments/deposit-policy`, { method: "PUT", body: JSON.stringify(body) });
  return res.json();
}

// ── Bills ─────────────────────────────────────────────────────────────────────
export interface BillLineItem { id: string; kind: string; label: string; unit_amount_cents: number; qty: number; amount_cents: number; taxable: number; product_id: string | null; service_id: string | null }
export interface Bill { id: string; appointment_id: string | null; customer_id: string | null; currency: string; subtotal_cents: number; tip_cents: number; tax_cents: number; total_cents: number; amount_paid_cents: number; status: string }
type BillResp = { bill: Bill; line_items?: BillLineItem[] };

export async function getOrCreateApptBill(slug: string, appointmentId: string): Promise<BillResp> {
  const res = await authFetch(`/${slug}/bills/appointment/${appointmentId}`, { method: "POST" });
  return res.json();
}
export async function createStandaloneBill(slug: string, customerId?: string): Promise<BillResp> {
  const res = await authFetch(`/${slug}/bills`, { method: "POST", body: JSON.stringify({ customer_id: customerId }) });
  return res.json();
}
export async function getBill(slug: string, id: string): Promise<BillResp> {
  const res = await authFetch(`/${slug}/bills/${id}`);
  return res.json();
}
export async function addBillLine(slug: string, id: string, body: { kind: string; label: string; unit_amount_cents: number; qty?: number; taxable?: boolean; product_id?: string }): Promise<BillResp> {
  const res = await authFetch(`/${slug}/bills/${id}/line-items`, { method: "POST", body: JSON.stringify(body) });
  return res.json();
}
export async function removeBillLine(slug: string, id: string, lineId: string): Promise<BillResp> {
  const res = await authFetch(`/${slug}/bills/${id}/line-items/${lineId}`, { method: "DELETE" });
  return res.json();
}
export async function setBillTip(slug: string, id: string, tipCents: number): Promise<{ bill: Bill }> {
  const res = await authFetch(`/${slug}/bills/${id}/tip`, { method: "PUT", body: JSON.stringify({ tip_cents: tipCents }) });
  return res.json();
}
export async function markBillPaid(slug: string, id: string, amountCents: number, method: "cash" | "bank" | "other"): Promise<{ bill: Bill }> {
  const res = await authFetch(`/${slug}/bills/${id}/mark-paid`, { method: "POST", body: JSON.stringify({ amount_cents: amountCents, method }) });
  return res.json();
}

// ── Tax config ────────────────────────────────────────────────────────────────
export type TaxConfig = { tax_enabled: boolean; tax_rate_bps: number; tax_label: string | null };
export async function getTaxConfig(slug: string): Promise<TaxConfig> {
  const res = await authFetch(`/${slug}/payments/tax-config`);
  return res.json();
}
export async function setTaxConfig(slug: string, body: TaxConfig): Promise<{ success: boolean }> {
  const res = await authFetch(`/${slug}/payments/tax-config`, { method: "PUT", body: JSON.stringify(body) });
  return res.json();
}

// ── Settings ──────────────────────────────────────────────────────────────────

export async function getSettings(slug: string) {
  const res = await authFetch(`/${slug}/settings`);
  return res.json();
}

export async function updateProfile(slug: string, body: unknown) {
  const res = await authFetch(`/${slug}/settings/profile`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  return res.json();
}

export async function updateScheduling(slug: string, body: unknown) {
  const res = await authFetch(`/${slug}/settings/scheduling`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  return res.json();
}

export async function getEmailConfig(slug: string) {
  const res = await authFetch(`/${slug}/email-config`);
  return res.json();
}

export async function updateEmailConfig(slug: string, body: unknown) {
  const res = await authFetch(`/${slug}/email-config`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  return res.json();
}

export async function testEmail(slug: string, body: unknown) {
  const res = await authFetch(`/${slug}/email-config/test`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return res.json();
}

// ── Team ──────────────────────────────────────────────────────────────────────

export async function getTeam(slug: string) {
  const res = await authFetch(`/${slug}/team`);
  return res.json();
}

export async function inviteMember(slug: string, body: unknown) {
  const res = await authFetch(`/${slug}/team/invite`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return res.json();
}

export async function revokeInvitation(slug: string, inviteId: string) {
  await authFetch(`/${slug}/team/invitations/${inviteId}`, { method: "DELETE" });
}

export async function removeMember(slug: string, memberId: string) {
  await authFetch(`/${slug}/team/members/${memberId}`, { method: "DELETE" });
}

// ── Billing ───────────────────────────────────────────────────────────────────

export async function getBilling(slug: string) {
  const res = await authFetch(`/${slug}/billing`);
  return res.json();
}

export async function getBillingPortal(slug: string) {
  const res = await authFetch(`/${slug}/billing/portal`, { method: "POST" });
  return res.json();
}

// ── Messaging providers (per-tenant email + SMS) ─────────────────────────────

export type MessagingChannel = "email" | "sms";

export interface MessagingState {
  email: { provider: string; config: Record<string, unknown> } | null;
  sms: { provider: string; config: Record<string, unknown> } | null;
}

export async function getMessaging(slug: string): Promise<MessagingState> {
  const res = await authFetch(`/${slug}/messaging`);
  return res.json() as Promise<MessagingState>;
}

export async function saveMessagingProvider(
  slug: string,
  channel: MessagingChannel,
  body: { provider: string; config: Record<string, unknown> },
): Promise<{ id: string; channel: string; provider: string }> {
  const res = await authFetch(`/${slug}/messaging/${channel}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
  return res.json() as Promise<{ id: string; channel: string; provider: string }>;
}

export interface MessagingFailure {
  id: string;
  channel: string;
  provider: string;
  to_target: string;
  subject: string | null;
  error: string;
  created_at: string;
}

export async function getMessagingFailures(
  slug: string,
): Promise<{ recent_count: number; recent: MessagingFailure[] }> {
  const res = await authFetch(`/${slug}/messaging/failures`);
  return res.json() as Promise<{ recent_count: number; recent: MessagingFailure[] }>;
}

// Per-event channel toggles ("send confirmation by email + SMS / email only / …").

export type LifecycleEvent = "booking_confirmation" | "booking_cancellation" | "booking_reminder";

export interface EventChannels {
  id: LifecycleEvent;
  email: boolean;
  sms: boolean;
}

export async function getMessagingEvents(slug: string): Promise<{ events: EventChannels[] }> {
  const res = await authFetch(`/${slug}/messaging/events`);
  return res.json() as Promise<{ events: EventChannels[] }>;
}

export async function saveMessagingEvent(
  slug: string,
  event: LifecycleEvent,
  body: { email: boolean; sms: boolean },
): Promise<{ ok: boolean; event: LifecycleEvent; email: boolean; sms: boolean }> {
  const res = await authFetch(`/${slug}/messaging/events/${event}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
  return res.json() as Promise<{ ok: boolean; event: LifecycleEvent; email: boolean; sms: boolean }>;
}

export type TemplateKind = "email" | "sms";
export interface TemplateOverride {
  template_id: string;
  subject: string | null;
  html: string | null;
  text: string;
  updated_at: string;
}
export interface TemplateDefault {
  subject?: string;
  html?: string;
  text: string;
}
export interface TemplatesState {
  overrides: Record<string, TemplateOverride>;
  defaults: Record<string, TemplateDefault>;
  available: Array<{ id: string; kind: TemplateKind }>;
}

export async function getMessagingTemplates(slug: string): Promise<TemplatesState> {
  const res = await authFetch(`/${slug}/messaging/templates`);
  return res.json() as Promise<TemplatesState>;
}

export async function saveMessagingTemplate(
  slug: string,
  templateId: string,
  body: { subject?: string; html?: string; text: string },
): Promise<{ ok: boolean; template_id: string }> {
  const res = await authFetch(`/${slug}/messaging/templates/${templateId}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
  return res.json() as Promise<{ ok: boolean; template_id: string }>;
}

export async function resetMessagingTemplate(
  slug: string,
  templateId: string,
): Promise<{ ok: boolean }> {
  const res = await authFetch(`/${slug}/messaging/templates/${templateId}`, {
    method: "DELETE",
  });
  return res.json() as Promise<{ ok: boolean }>;
}

export async function sendMessagingTest(
  slug: string,
  body: { channel: MessagingChannel; to: string },
): Promise<{ ok: boolean; providerMessageId?: string }> {
  const res = await authFetch(`/${slug}/messaging/test`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return res.json() as Promise<{ ok: boolean; providerMessageId?: string }>;
}

// ── Places API (server-side proxy) ────────────────────────────────────────────

// The dashboard never holds the Google Places API key. These helpers call the
// gateway worker (api.zedule.app/api/dashboard/places/*) which forwards to
// Google with X-Goog-Api-Key set from a Worker secret.

export interface PlacesAutocompleteSuggestion {
  placeId: string;
  mainText: string;
  secondaryText: string;
}

export interface PlacesDetails {
  id: string;
  displayName: string;
  formattedAddress: string;
  addressComponents: Array<{ longText: string; shortText: string; types: string[] }>;
  internationalPhoneNumber: string;
  nationalPhoneNumber: string;
  websiteUri: string;
  location: { latitude?: number; longitude?: number } | null;
}

async function placesFetch<T>(path: string, body: unknown): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (DEV_BYPASS_USER_ID) {
    headers["X-Dev-Bypass"] = DEV_BYPASS_USER_ID;
  } else {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) headers["Authorization"] = `Bearer ${session.access_token}`;
  }
  const res = await fetch(`${API_BASE}/api/dashboard/places/${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: res.statusText }));
    const e = (errBody as { error?: unknown }).error;
    const msg = typeof e === "string"
      ? e
      : e && typeof e === "object" && "message" in (e as object)
        ? String((e as { message: unknown }).message)
        : res.statusText;
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

export function placesAutocomplete(input: string, sessionToken: string) {
  return placesFetch<{ suggestions: PlacesAutocompleteSuggestion[] }>(
    "autocomplete",
    { input, sessionToken },
  );
}

export function placesDetails(placeId: string, sessionToken: string) {
  return placesFetch<PlacesDetails>("details", { placeId, sessionToken });
}

// ── Business creation (self-serve trial) ──────────────────────────────────────

export interface CreateBusinessPayload {
  name: string;
  slug: string;
  region?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  phone?: string;
  email?: string;
  website_url?: string;
  google_place_id?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
}

export async function createBusiness(payload: CreateBusinessPayload): Promise<{ slug: string; trial_ends_at: string }> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (DEV_BYPASS_USER_ID) {
    headers["X-Dev-Bypass"] = DEV_BYPASS_USER_ID;
  } else {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) headers["Authorization"] = `Bearer ${session.access_token}`;
  }
  const res = await fetch(`${API_BASE}/api/dashboard/businesses`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const e = (body as { error?: unknown }).error;
    const msg = typeof e === "string"
      ? e
      : e && typeof e === "object" && "message" in (e as object)
        ? String((e as { message: unknown }).message)
        : res.statusText;
    throw new Error(msg);
  }
  return res.json() as Promise<{ slug: string; trial_ends_at: string }>;
}

// ── Legal acceptance ──────────────────────────────────────────────────────────

// Posts to the gateway (no slug — it's a global, per-user record). The server
// stamps accepted_at + IP and writes to Supabase user metadata.
export async function recordLegalAcceptance(payload: {
  version: string;
  terms_url: string;
  privacy_url: string;
}): Promise<void> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (DEV_BYPASS_USER_ID) {
    headers["X-Dev-Bypass"] = DEV_BYPASS_USER_ID;
  } else {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) headers["Authorization"] = `Bearer ${session.access_token}`;
  }
  const res = await fetch(`${API_BASE}/api/dashboard/legal/accept`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const e = (body as { error?: unknown }).error;
    const msg =
      typeof e === "string"
        ? e
        : e && typeof e === "object" && "message" in (e as object)
          ? String((e as { message: unknown }).message)
          : res.statusText;
    throw new Error(msg);
  }
  // Refresh the session so the in-memory user_metadata picks up the new entry
  // and the gate dismisses on the next render without a hard reload.
  await supabase.auth.refreshSession();
}

// ── Claude / MCP ──────────────────────────────────────────────────────────────

export const MCP_CONNECTOR_URL = `${API_BASE}/mcp`;

export async function getClaudeStatus(): Promise<{ connected: boolean; connectionCount: number }> {
  const res = await authFetch("/claude/status");
  return res.json() as Promise<{ connected: boolean; connectionCount: number }>;
}

export async function disconnectClaude(): Promise<{ connected: boolean }> {
  const res = await authFetch("/claude/disconnect", { method: "POST" });
  return res.json() as Promise<{ connected: boolean }>;
}

export { authFetch };
